import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Modal, OverlayTrigger, Row, Spinner, Tab, Table, Tabs, Tooltip } from 'react-bootstrap'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { URL as BACKEND_URL, fetchNewToken } from '../../gql/client'
import { STORAGE_NAMES } from '../../helpers/consts'
import { useTelephony } from '../../hooks/useTelephony'
import {
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_STATE_REQUEST_EVENT,
  TELEPHONY_DRAWER_TOGGLE_EVENT,
  TelephonyDrawerStateDetail,
} from '../../types/telephony'

// Ask the drawer for its current state; if collapsed (or hidden), toggle it
// open so the supervisor sees VICIphone and can answer the monitor ring.
const ensureDrawerExpanded = () => {
  let settled = false
  const handle = (event: Event) => {
    if (settled) return
    settled = true
    window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
    const detail = (event as CustomEvent<TelephonyDrawerStateDetail>).detail
    if (!detail?.visible) {
      window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_TOGGLE_EVENT))
    }
  }
  window.addEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
  window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_STATE_REQUEST_EVENT))
  window.setTimeout(() => {
    if (settled) return
    settled = true
    window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
    window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_TOGGLE_EVENT))
  }, 300)
}

interface LoggedInAgent {
  user: string
  campaign_id: string
  conf_exten: string
  status: string
  lead_id?: string
  callerid?: string
  calls_today: string
  policytools_calls_today?: string
  full_name: string
  user_group: string
  user_level?: string
  // From vicidial_live_agents.last_state_change via backend enrichment;
  // exact seconds the agent has been in the current status.
  sec_in_state?: number
  last_state_change?: string
  // Pause reason: raw code (BATH/LUNCH/SHORT/LONG/LOGIN) + friendly name.
  pause_code?: string
  pause_code_name?: string
  // Live call audio quality (RTP) — null when the agent has no active media.
  audio?: {
    codec: string
    recv_loss_pct: number
    send_loss_pct: number
    jitter_ms: number
    quality: 'good' | 'fair' | 'poor'
  } | null
  // Behavioral "can't-hear" flag from today's recordings/dispositions.
  behavior?: {
    calls_today: number
    avg_len_s: number
    max_len_s: number
    vmail_pct: number
    flagged: boolean
    flag_reason: string
  } | null
}

interface DashboardResponse {
  provider: string
  agents: LoggedInAgent[]
  calls_in_queue_count: number
  raw_response: string
}

interface AgentMetric {
  agent_user: string
  total_calls: number
  dispositioned: number
  real_conversations: number
  dispo_rate: number
  conversation_rate: number
  avg_talk_time_seconds: number
  calls_per_hour: number
  sales?: number
  sale_rate?: number
}

interface CampaignMetric {
  campaign_id: string
  total_calls: number
  dispositioned: number
  real_conversations: number
  dispo_rate: number
  conversation_rate: number
  avg_talk_time_seconds: number
  calls_per_hour: number
  sales?: number
  sale_rate?: number
}

interface DispositionBreakdown {
  disposition: string
  count: number
}

interface DayMetric {
  day: string
  total_calls: number
  real_conversations: number
  total_talk_seconds: number
  avg_talk_time_seconds: number
}

interface MetricsResponse {
  since_hours: number
  total_calls: number
  dispositioned: number
  real_conversations: number
  real_conversation_threshold_seconds?: number
  dispo_rate: number
  conversation_rate: number
  avg_talk_time_seconds: number
  calls_per_hour: number
  sales?: number
  sale_rate?: number
  callbacks?: number
  callback_rate?: number
  per_agent: AgentMetric[]
  per_campaign: CampaignMetric[]
  per_day?: DayMetric[]
  dispositions: DispositionBreakdown[]
}

interface CallLogEntry {
  start_time: string
  agent: string
  campaign: string
  phone: string
  duration: number
  status: string
  term_reason: string
  uniqueid: string
  recording_id: string
  recording_url: string
  lead_id?: string
  first_name?: string
  last_name?: string
  lead_comments?: string
}

interface CallLogAgent {
  user: string
  full_name: string
}

interface CallLogResponse {
  calls: CallLogEntry[]
  agents?: CallLogAgent[]
  total: number
  limit: number
  offset: number
  date_from: string
  date_to: string
}

interface DialerCampaign {
  campaign_id: string
  campaign_name: string
  dial_method: string
  dial_level: string
  target_drop_pct: number
  drop_call_seconds: number
  calls_today: number
  answers_today: number
  drops_today: number
  drop_pct: number
  agent_calls_today: number
  machines_today: number
  nodisp_today: number
  hopper: number
  dialable_leads: number
  avg_agents: number
  dl_diff: string
  trunk_short: number
  trunk_fill: number
  hopper_level: number
  lead_order: string
  statuses: string
  agents: {
    total: number
    ready: number
    oncall: number
    paused: number
    other: number
  }
}

interface DialerHealthResponse {
  campaigns: DialerCampaign[]
  as_of: string
}

interface AgentPerfRow {
  user: string
  full_name: string
  calls: number
  real_convos: number
  talk_sec: number
  avg_talk_sec: number
  sales: number
  dispositions: Record<string, number>
  // Pause time (from vicidial_agent_log): total seconds + per-reason breakdown.
  pause_sec_total?: number
  pause_by_reason?: Record<string, { sec: number; cnt: number }>
}

interface AgentPerfResponse {
  agents: AgentPerfRow[]
  date_from: string
  date_to: string
  sale_dispositions: string[]
  user_groups: { user_group: string; group_name: string }[]
  // Friendly labels for pause codes (BATH->Bathroom, etc.).
  pause_code_labels?: Record<string, string>
}

interface OutboundReport {
  date_from: string
  date_to: string
  total_calls: number
  avg_call_len: number
  human_answers: number
  ha_avg_len: number
  ha_total_sec: number
  machines: number
  drops: number
  pdrops: number
  drop_pct_calls: number
  drop_pct_answers: number
  no_answers: number
  sales: number
  nodisp: number
  hangup_reasons: { reason: string; calls: number }[]
  dispositions: { status: string; count: number }[]
}

interface ListsReport {
  lists: {
    list_id: string
    list_name: string
    campaign_id: string
    calls: number
    contacts: number
    sales: number
    nodisp: number
    contact_rate: number
    sale_rate: number
  }[]
  date_from: string
  date_to: string
}

interface InboundReport {
  date_from: string
  date_to: string
  sl_seconds: number
  overall: {
    total: number
    answered: number
    abandoned: number
    avg_wait_sec: number
    service_level: number
    abandon_pct: number
  }
  groups: {
    in_group: string
    total: number
    answered: number
    abandoned: number
    avg_wait_sec: number
    service_level: number
  }[]
}

interface SupervisorUser {
  agent_user: string
  full_name: string
  user_group: string
  live: boolean
}

interface SupervisorUsersResponse {
  since_days: number
  users: SupervisorUser[]
}

interface SupervisorLogoutResponse {
  provider: string
  agent_user: string
  target_agent_user?: string
  supervisor_user?: string
  active_session: boolean
  status: string
  status_before_disconnect: string
  campaign_id: string
  session_id: string
  phone_number: string
  raw_response: string
  hangup_raw_response: string
}

type SupervisorBulkAction = 'logout' | 'pause' | 'resume'
type SupervisorMonitorStage = 'MONITOR' | 'BARGE' | 'WHISPER'

interface PendingSupervisorAction {
  action: SupervisorBulkAction
  agentUsers: string[]
}

interface SupervisorMonitorRequest {
  agentUser: string
  stage: SupervisorMonitorStage
  requestId: number
}

interface TelephonySupervisorLocationState {
  pendingSupervisorMonitor?: SupervisorMonitorRequest
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

const authGet = async <T,>(url: string): Promise<T> => {
  try {
    const res = await axios.get<T>(url, { headers: authHeaders() })
    return res.data
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        const res = await axios.get<T>(url, { headers: { Authorization: `Bearer ${newToken}` } })
        return res.data
      }
    }
    throw err
  }
}

const authPost = async <T,>(url: string, body: any): Promise<T> => {
  try {
    const res = await axios.post<T>(url, body, { headers: authHeaders() })
    return res.data
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        const res = await axios.post<T>(url, body, {
          headers: { Authorization: `Bearer ${newToken}` },
        })
        return res.data
      }
    }
    throw err
  }
}

const STATUS_VARIANT: Record<string, string> = {
  READY: 'success',
  INCALL: 'primary',
  QUEUE: 'info',
  PAUSED: 'warning',
  CLOSER: 'success',
  DEAD: 'danger',
  DISPO: 'secondary',
}

const STATUS_BADGE_STYLE: Record<string, CSSProperties> = {
  READY: { backgroundColor: '#DCFCE7', color: '#166534' },
  INCALL: { backgroundColor: '#DBEAFE', color: '#1D4ED8' },
  QUEUE: { backgroundColor: '#E0F2FE', color: '#0369A1' },
  PAUSED: { backgroundColor: '#FEF3C7', color: '#B45309' },
  CLOSER: { backgroundColor: '#DCFCE7', color: '#166534' },
  DEAD: { backgroundColor: '#FEE2E2', color: '#DC2626' },
  DISPO: { backgroundColor: '#E5E7EB', color: '#4B5563' },
}

const normalizeStatus = (status: string) => String(status || '').trim().toUpperCase()
const LIVE_WAITING_STATUSES = new Set(['READY', 'CLOSER'])

// Report hub menu (VICIdial-style "Server Stats and Reports"). Each entry opens a
// report view inside the Reports tab. `available` gates the not-yet-built ones.
const REPORT_MENU: { key: string; title: string; desc: string; icon: string; group: string; available: boolean }[] = [
  { key: 'agentperf', group: 'Agents', title: 'Agent Performance', icon: 'bi bi-person-badge', available: true,
    desc: 'Per-agent calls, real conversations, talk time, sales and disposition mix.' },
  { key: 'outbound', group: 'Outbound', title: 'Outbound Calling Report', icon: 'bi bi-telephone-outbound', available: true,
    desc: "Today's outbound totals: calls, human answers, machines, drops, hangup reasons." },
  { key: 'lists', group: 'Outbound', title: 'Lists / Lead Source', icon: 'bi bi-list-ul', available: true,
    desc: 'Contact and sale performance by list / lead source.' },
  { key: 'inbound', group: 'Inbound', title: 'Inbound Report', icon: 'bi bi-telephone-inbound', available: true,
    desc: 'Inbound volume, service level, answered vs abandoned and wait times.' },
]

const formatSeconds = (seconds: number) => {
  if (!seconds || seconds <= 0) return '0s'
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (!mins) return `${secs}s`
  return `${mins}m ${secs}s`
}

const formatPercent = (ratio: number) => `${(ratio * 100).toFixed(1)}%`

// Total conversation time as "Xh YYm" (e.g. 6h 28m).
const formatDuration = (seconds: number) => {
  const total = Math.max(0, Math.round(seconds || 0))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  return `${h}h ${String(m).padStart(2, '0')}m`
}

// "2026-06-01" -> "Jun 1" for compact day labels.
const formatDayLabel = (day: string) => {
  const parsed = new Date(`${day}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return day
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const REFRESH_INTERVAL_MS = 4000
const CALL_LOG_LIMIT = 50
// VICIdial dispositions / dialer statuses available as the Call log "Result" filter.
const CALL_LOG_RESULTS = ['SALE', 'NCSE', 'INST', 'CALLBK', 'NINT', 'NANS', 'DROP', 'QUEUE', 'PDROP', 'DNC', 'DEC', 'AA']
type LiveAgentsSortKey =
  | 'user'
  | 'full_name'
  | 'campaign_id'
  | 'conf_exten'
  | 'status'
  | 'sec_in_state'
  | 'user_group'
  | 'calls_today'
type LiveAgentsSortDirection = 'asc' | 'desc'

const compareSupervisorStrings = (left?: string | number | null, right?: string | number | null) =>
  String(left || '')
    .trim()
    .localeCompare(String(right || '').trim(), undefined, {
      numeric: true,
      sensitivity: 'base',
    })

const compareSupervisorNumbers = (left?: string | number | null, right?: string | number | null) =>
  Number(left || 0) - Number(right || 0)

export const TelephonySupervisorPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const telephony = useTelephony()
  const hasAccess = telephony.config.vicidial_supervisor_access
  const resolved = telephony.resolved
  const handledMonitorRequestRef = useRef(0)

  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null)
  const [dashboardError, setDashboardError] = useState('')
  const [loadingDashboard, setLoadingDashboard] = useState(false)

  const [metrics, setMetrics] = useState<MetricsResponse | null>(null)
  const [metricsError, setMetricsError] = useState('')
  const [loadingMetrics, setLoadingMetrics] = useState(false)

  const [supervisorUsers, setSupervisorUsers] = useState<SupervisorUser[]>([])

  const [callLog, setCallLog] = useState<CallLogEntry[]>([])
  const [callLogTotal, setCallLogTotal] = useState(0)
  const [callLogOffset, setCallLogOffset] = useState(0)
  const [loadingCallLog, setLoadingCallLog] = useState(false)
  const [callLogError, setCallLogError] = useState('')
  const [clDateFrom, setClDateFrom] = useState('')
  const [clDateTo, setClDateTo] = useState('')
  const [clAgent, setClAgent] = useState('')
  const [clPhone, setClPhone] = useState('')
  const [clCustomer, setClCustomer] = useState('')
  const [clCampaign, setClCampaign] = useState('')
  const [clStatus, setClStatus] = useState('')
  const [callLogAgents, setCallLogAgents] = useState<CallLogAgent[]>([])

  const [dialerHealth, setDialerHealth] = useState<DialerHealthResponse | null>(null)
  const [loadingDialer, setLoadingDialer] = useState(false)
  const [dialerError, setDialerError] = useState('')
  const [dialerCampaign, setDialerCampaign] = useState('') // '' = all active campaigns

  const [reportView, setReportView] = useState('') // '' = report menu, else report key

  const [agentPerf, setAgentPerf] = useState<AgentPerfResponse | null>(null)
  const [loadingPerf, setLoadingPerf] = useState(false)
  const [perfError, setPerfError] = useState('')
  const [perfDateFrom, setPerfDateFrom] = useState('')
  const [perfDateTo, setPerfDateTo] = useState('')
  const [perfCampaign, setPerfCampaign] = useState('')
  const [perfGroup, setPerfGroup] = useState('')
  const [perfAgent, setPerfAgent] = useState('')

  const [outbound, setOutbound] = useState<OutboundReport | null>(null)
  const [loadingOutbound, setLoadingOutbound] = useState(false)
  const [outboundError, setOutboundError] = useState('')
  const [obDateFrom, setObDateFrom] = useState('')
  const [obDateTo, setObDateTo] = useState('')
  const [obCampaign, setObCampaign] = useState('')

  const [listsReport, setListsReport] = useState<ListsReport | null>(null)
  const [loadingLists, setLoadingLists] = useState(false)
  const [listsError, setListsError] = useState('')
  const [listsDateFrom, setListsDateFrom] = useState('')
  const [listsDateTo, setListsDateTo] = useState('')
  const [listsCampaign, setListsCampaign] = useState('')

  const [inboundReport, setInboundReport] = useState<InboundReport | null>(null)
  const [loadingInbound, setLoadingInbound] = useState(false)
  const [inboundError, setInboundError] = useState('')
  const [inDateFrom, setInDateFrom] = useState('')
  const [inDateTo, setInDateTo] = useState('')

  const [sinceHours, setSinceHours] = useState<number>(24)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [campaignFilter, setCampaignFilter] = useState('')
  const [agentFilter, setAgentFilter] = useState('')
  const [liveGroupFilter, setLiveGroupFilter] = useState('')
  const [liveCampaignFilter, setLiveCampaignFilter] = useState('')
  const [liveAgentsSort, setLiveAgentsSort] = useState<{
    key: LiveAgentsSortKey
    direction: LiveAgentsSortDirection
  }>({
    key: 'user',
    direction: 'asc',
  })

  const [monitoringAgent, setMonitoringAgent] = useState<string>('')
  const [selectedAgentUsers, setSelectedAgentUsers] = useState<string[]>([])
  const [runningSupervisorAction, setRunningSupervisorAction] = useState(false)
  const [pendingSupervisorAction, setPendingSupervisorAction] =
    useState<PendingSupervisorAction | null>(null)
  const [activeMonitor, setActiveMonitor] = useState<{
    targetAgentUser: string
    targetAgentName: string
    stage: SupervisorMonitorStage
    supervisorPreviousStatus: string
    supervisorPreviousPauseCode: string
  } | null>(null)
  const [stoppingMonitor, setStoppingMonitor] = useState(false)
  const [pendingCampaignChange, setPendingCampaignChange] = useState<{
    agentUser: string
    agentName: string
    fromCampaign: string
    toCampaign: string
  } | null>(null)
  const [changingCampaign, setChangingCampaign] = useState(false)
  // Tracks when each agent's status last changed (per user). Detected by
  // comparing successive dashboard snapshots; resets on status change.
  const agentStatusTimestampsRef = useRef<Record<string, { status: string; ts: number }>>({})
  const [nowTick, setNowTick] = useState(() => Date.now())

  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoadingDashboard(true)
    try {
      const data = await authGet<DashboardResponse>(
        `${BACKEND_URL}/api/telephony/supervisor/dashboard?provider=VICIDIAL`
      )
      setDashboard(data)
      setDashboardError('')
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load supervisor dashboard'
      setDashboardError(msg)
    } finally {
      if (!silent) setLoadingDashboard(false)
    }
  }, [])

  const loadSupervisorUsers = useCallback(async () => {
    try {
      const data = await authGet<SupervisorUsersResponse>(
        `${BACKEND_URL}/api/telephony/supervisor/users?since_days=90&provider=VICIDIAL`
      )
      setSupervisorUsers(data?.users || [])
    } catch {
      // Silently ignore — fallback to live agents in dropdown.
    }
  }, [])

  const loadMetrics = useCallback(async () => {
    setLoadingMetrics(true)
    try {
      const params = new URLSearchParams()
      params.set('since_hours', String(sinceHours || 24))
      // Explicit date range takes precedence over the relative window on the backend.
      if (dateFrom) params.set('date_from', dateFrom)
      if (dateTo) params.set('date_to', dateTo)
      if (campaignFilter) params.set('campaign_id', campaignFilter)
      if (agentFilter) params.set('agent_user', agentFilter)
      const data = await authGet<MetricsResponse>(
        `${BACKEND_URL}/api/telephony/supervisor/metrics?${params.toString()}`
      )
      setMetrics(data)
      setMetricsError('')
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load metrics'
      setMetricsError(msg)
    } finally {
      setLoadingMetrics(false)
    }
  }, [sinceHours, dateFrom, dateTo, campaignFilter, agentFilter])

  const loadCallLog = useCallback(
    async (offset = 0) => {
      setLoadingCallLog(true)
      try {
        const params = new URLSearchParams()
        if (clDateFrom) params.set('date_from', clDateFrom)
        if (clDateTo) params.set('date_to', clDateTo)
        if (clCampaign) params.set('campaign', clCampaign)
        if (clAgent) params.set('agent', clAgent)
        if (clPhone) params.set('phone', clPhone)
        if (clCustomer) params.set('customer', clCustomer)
        if (clStatus) params.set('status', clStatus)
        params.set('limit', String(CALL_LOG_LIMIT))
        params.set('offset', String(offset))
        const data = await authGet<CallLogResponse>(
          `${BACKEND_URL}/api/telephony/supervisor/calls?${params.toString()}`
        )
        setCallLog(data.calls || [])
        setCallLogTotal(data.total || 0)
        setCallLogOffset(offset)
        if (data.agents && data.agents.length) setCallLogAgents(data.agents)
        setCallLogError('')
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to load call log'
        setCallLogError(msg)
      } finally {
        setLoadingCallLog(false)
      }
    },
    [clDateFrom, clDateTo, clCampaign, clAgent, clPhone, clCustomer, clStatus]
  )

  const loadDialerHealth = useCallback(async (silent = false) => {
    if (!silent) setLoadingDialer(true)
    try {
      const data = await authGet<DialerHealthResponse>(
        `${BACKEND_URL}/api/telephony/supervisor/dialer-health?provider=VICIDIAL`
      )
      setDialerHealth(data)
      setDialerError('')
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load dialer health'
      setDialerError(msg)
    } finally {
      if (!silent) setLoadingDialer(false)
    }
  }, [])

  const loadAgentPerformance = useCallback(async () => {
    setLoadingPerf(true)
    try {
      const params = new URLSearchParams()
      if (perfDateFrom) params.set('date_from', perfDateFrom)
      if (perfDateTo) params.set('date_to', perfDateTo)
      if (perfCampaign) params.set('campaign', perfCampaign)
      if (perfGroup) params.set('user_group', perfGroup)
      if (perfAgent) params.set('agent', perfAgent)
      const data = await authGet<AgentPerfResponse>(
        `${BACKEND_URL}/api/telephony/supervisor/agent-performance?${params.toString()}`
      )
      setAgentPerf(data)
      setPerfError('')
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load agent performance'
      setPerfError(msg)
    } finally {
      setLoadingPerf(false)
    }
  }, [perfDateFrom, perfDateTo, perfCampaign, perfGroup, perfAgent])

  useEffect(() => {
    if (!hasAccess) return
    loadDashboard(false)
    loadMetrics()
    loadSupervisorUsers()
    loadDialerHealth(false)
  }, [hasAccess, loadDashboard, loadMetrics, loadSupervisorUsers, loadDialerHealth])

  const loadOutboundReport = useCallback(async () => {
    setLoadingOutbound(true)
    try {
      const params = new URLSearchParams()
      if (obDateFrom) params.set('date_from', obDateFrom)
      if (obDateTo) params.set('date_to', obDateTo)
      if (obCampaign) params.set('campaign', obCampaign)
      const data = await authGet<OutboundReport>(
        `${BACKEND_URL}/api/telephony/supervisor/outbound-report?${params.toString()}`
      )
      setOutbound(data)
      setOutboundError('')
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load outbound report'
      setOutboundError(msg)
    } finally {
      setLoadingOutbound(false)
    }
  }, [obDateFrom, obDateTo, obCampaign])

  // Agent performance: initial load; afterwards refreshed via the Apply button.
  useEffect(() => {
    if (hasAccess) loadAgentPerformance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess])

  const loadListsReport = useCallback(async () => {
    setLoadingLists(true)
    try {
      const params = new URLSearchParams()
      if (listsDateFrom) params.set('date_from', listsDateFrom)
      if (listsDateTo) params.set('date_to', listsDateTo)
      if (listsCampaign) params.set('campaign', listsCampaign)
      const data = await authGet<ListsReport>(
        `${BACKEND_URL}/api/telephony/supervisor/lists-report?${params.toString()}`
      )
      setListsReport(data)
      setListsError('')
    } catch (err: any) {
      setListsError(err?.response?.data?.error || err?.message || 'Failed to load lists report')
    } finally {
      setLoadingLists(false)
    }
  }, [listsDateFrom, listsDateTo, listsCampaign])

  const loadInboundReport = useCallback(async () => {
    setLoadingInbound(true)
    try {
      const params = new URLSearchParams()
      if (inDateFrom) params.set('date_from', inDateFrom)
      if (inDateTo) params.set('date_to', inDateTo)
      const data = await authGet<InboundReport>(
        `${BACKEND_URL}/api/telephony/supervisor/inbound-report?${params.toString()}`
      )
      setInboundReport(data)
      setInboundError('')
    } catch (err: any) {
      setInboundError(err?.response?.data?.error || err?.message || 'Failed to load inbound report')
    } finally {
      setLoadingInbound(false)
    }
  }, [inDateFrom, inDateTo])

  // Lazy-load each report the first time it's opened from the Reports menu.
  useEffect(() => {
    if (!hasAccess) return
    if (reportView === 'outbound' && !outbound && !loadingOutbound) loadOutboundReport()
    if (reportView === 'lists' && !listsReport && !loadingLists) loadListsReport()
    if (reportView === 'inbound' && !inboundReport && !loadingInbound) loadInboundReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportView, hasAccess])

  // Auto-apply report dropdown filters on change (date fields still use Apply).
  // No mount-skip guard is needed: on mount reportView is '' (the menu), so every
  // gate below is false and nothing loads twice. A report only loads when its view
  // is open AND a filter actually changes — exactly when the user picks a value.
  useEffect(() => {
    if (!hasAccess) return
    if (reportView === 'agentperf') loadAgentPerformance()
    if (reportView === 'outbound') loadOutboundReport()
    if (reportView === 'lists') loadListsReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfCampaign, perfGroup, perfAgent, obCampaign, listsCampaign])

  // Dialer health auto-refresh (lightweight, every 10s).
  useEffect(() => {
    if (!hasAccess) return
    const id = setInterval(() => loadDialerHealth(true), 10000)
    return () => clearInterval(id)
  }, [hasAccess, loadDialerHealth])

  // Initial Call log load; afterwards filters are applied manually via the Apply button.
  useEffect(() => {
    if (hasAccess) loadCallLog(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess])

  useEffect(() => {
    if (!hasAccess) return
    const id = setInterval(() => loadDashboard(true), REFRESH_INTERVAL_MS)
    return () => clearInterval(id)
  }, [hasAccess, loadDashboard])

  // Re-evaluate "time in state" timestamps every dashboard refresh. If the
  // backend provides `sec_in_state` (real value from vicidial_live_agents),
  // use it as the anchor; otherwise fall back to first-seen client-side time.
  useEffect(() => {
    const agents = dashboard?.agents
    if (!agents || agents.length === 0) return
    const now = Date.now()
    const next = { ...agentStatusTimestampsRef.current }
    let changed = false
    agents.forEach((a) => {
      const status = normalizeStatus(a.status)
      const prev = next[a.user]
      if (!prev) {
        // First time we see this agent: best-effort anchor from the backend value
        // (vicidial last_state_change). It can be stale in the PolicyTools flow, but
        // it's the only estimate for a state that began before this page loaded.
        const backendSec =
          typeof a.sec_in_state === 'number' && a.sec_in_state >= 0 ? a.sec_in_state : null
        next[a.user] = { status, ts: backendSec !== null ? now - backendSec * 1000 : now }
        changed = true
      } else if (prev.status !== status) {
        // We OBSERVED a real status change -> reset the timer to now. The native
        // vicidial_live_agents.last_state_change is not maintained on every
        // transition in the PolicyTools/MeetMe flow, so we trust our own
        // observation over the backend's (often stale) sec_in_state.
        next[a.user] = { status, ts: now }
        changed = true
      }
      // Same status as last poll: keep the existing anchor and let it count up.
    })
    if (changed) {
      agentStatusTimestampsRef.current = next
    }
  }, [dashboard])

  // Tick once per second so the time-in-state column updates live.
  useEffect(() => {
    if (!hasAccess) return
    const id = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(id)
  }, [hasAccess])

  const formatTimeInState = useCallback(
    (user: string) => {
      const entry = agentStatusTimestampsRef.current[user]
      if (!entry) return '—'
      const seconds = Math.max(0, Math.floor((nowTick - entry.ts) / 1000))
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m}:${String(s).padStart(2, '0')}`
    },
    [nowTick]
  )

  const getTimeInStateSeconds = useCallback(
    (user: string) => {
      const entry = agentStatusTimestampsRef.current[user]
      if (!entry) return -1
      return Math.max(0, Math.floor((nowTick - entry.ts) / 1000))
    },
    [nowTick]
  )

  const queryDrawerState = useCallback(
    (timeoutMs = 250) =>
      new Promise<TelephonyDrawerStateDetail | null>((resolve) => {
        let settled = false
        const handle = (event: Event) => {
          if (settled) return
          settled = true
          window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
          resolve((event as CustomEvent<TelephonyDrawerStateDetail>).detail || null)
        }
        window.addEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
        window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_STATE_REQUEST_EVENT))
        window.setTimeout(() => {
          if (settled) return
          settled = true
          window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
          resolve(null)
        }, timeoutMs)
      }),
    []
  )

  const ensureSupervisorMonitorReady = useCallback(async () => {
    if (!telephony.config.vicidial_enabled) {
      toast.error('PolicyDial is not enabled for this account.')
      return false
    }

    if (!telephony.config.vicidial_phone_login) {
      toast.error('No PolicyDial phone login is configured for this supervisor account.')
      return false
    }

    const drawerState = await queryDrawerState()
    const drawerAlreadyMounted = Boolean(
      drawerState?.mounted && drawerState?.provider === 'VICIDIAL'
    )

    if (drawerAlreadyMounted) {
      if (!drawerState?.visible) {
        ensureDrawerExpanded()
      }
      return true
    }

    const webphoneResponse = await telephony.openWebphone('VICIDIAL', {
      preferAgentSession: false,
      phoneOnly: true,
    })
    if (!webphoneResponse) {
      return false
    }

    // Give the softphone a short head start so the monitor originate can ring
    // the supervisor extension instead of racing the webphone boot.
    await new Promise<void>((resolve) =>
      window.setTimeout(resolve, webphoneResponse.source === 'autologin' ? 1600 : 900)
    )
    ensureDrawerExpanded()
    return true
  }, [queryDrawerState, telephony])

  const startMonitor = useCallback(
    async (agentUser: string, stage: SupervisorMonitorStage) => {
      if (!agentUser) return
      if (activeMonitor) {
        toast.info('Stop the active monitor session before starting a new one.')
        return
      }
      setMonitoringAgent(agentUser)
      try {
        const monitorReady = await ensureSupervisorMonitorReady()
        if (!monitorReady) {
          return
        }
        const response = await authPost<{
          supervisor_previous_status?: string
          supervisor_previous_pause_code?: string
        }>(`${BACKEND_URL}/api/telephony/supervisor/monitor`, {
          agent_user: agentUser,
          stage,
          provider: 'VICIDIAL',
        })
        const targetName =
          dashboard?.agents?.find((a) => a.user === agentUser)?.full_name ||
          supervisorUsers.find((u) => u.agent_user === agentUser)?.full_name ||
          agentUser
        setActiveMonitor({
          targetAgentUser: agentUser,
          targetAgentName: targetName,
          stage,
          supervisorPreviousStatus: response?.supervisor_previous_status || '',
          supervisorPreviousPauseCode: response?.supervisor_previous_pause_code || '',
        })
        // Force the PolicyDial drawer open so the supervisor sees VICIphone
        // and can answer the incoming monitor call (webphone_auto_answer=N).
        ensureDrawerExpanded()
        toast.success(`${stage} started for ${agentUser}. Answer in PolicyDial when it rings.`)
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || `${stage} failed`
        toast.error(msg)
      } finally {
        setMonitoringAgent('')
      }
    },
    [activeMonitor, dashboard, ensureSupervisorMonitorReady, supervisorUsers]
  )

  // Legacy support: if another page (e.g. /integrations) navigates here with a
  // pendingSupervisorMonitor state, still honor it. New path no longer uses this.
  useEffect(() => {
    const pendingRequest = (location.state as TelephonySupervisorLocationState | null)
      ?.pendingSupervisorMonitor
    if (!hasAccess || !pendingRequest?.requestId) return
    if (handledMonitorRequestRef.current === pendingRequest.requestId) return

    handledMonitorRequestRef.current = pendingRequest.requestId
    navigate('/telephony-supervisor', { replace: true })
    void startMonitor(pendingRequest.agentUser, pendingRequest.stage)
  }, [hasAccess, location.state, navigate, startMonitor])

  const handleMonitor = useCallback(
    (agentUser: string, stage: SupervisorMonitorStage) => {
      if (!agentUser) return
      if (activeMonitor) {
        toast.info('Stop the active monitor session before starting a new one.')
        return
      }
      void startMonitor(agentUser, stage)
    },
    [activeMonitor, startMonitor]
  )

  const handleStopMonitor = useCallback(async () => {
    if (!activeMonitor || stoppingMonitor) return
    const snapshot = activeMonitor
    setStoppingMonitor(true)
    try {
      await authPost(`${BACKEND_URL}/api/telephony/supervisor/monitor/stop`, {
        provider: 'VICIDIAL',
        previous_status: snapshot.supervisorPreviousStatus,
        previous_pause_code: snapshot.supervisorPreviousPauseCode,
      })
      toast.success(`${snapshot.stage} ended. Supervisor agent restored.`)
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Could not stop monitor cleanly'
      toast.warning(`${msg}. Hang up in PolicyDial if the call is still active.`)
    } finally {
      setActiveMonitor(null)
      setStoppingMonitor(false)
    }
  }, [activeMonitor, stoppingMonitor])

  const requestSupervisorAction = useCallback((action: SupervisorBulkAction, agentUsers: string[]) => {
    const uniqueAgentUsers = Array.from(
      new Set(agentUsers.map((agentUser) => String(agentUser || '').trim()).filter(Boolean))
    )
    if (!uniqueAgentUsers.length) return
    setPendingSupervisorAction({ action, agentUsers: uniqueAgentUsers })
  }, [])

  const confirmChangeCampaign = useCallback(async () => {
    if (!pendingCampaignChange) return
    setChangingCampaign(true)
    try {
      await authPost(`${BACKEND_URL}/api/telephony/supervisor/change-campaign`, {
        provider: 'VICIDIAL',
        agent_user: pendingCampaignChange.agentUser,
        campaign_id: pendingCampaignChange.toCampaign,
      })
      toast.success(
        `${pendingCampaignChange.agentName} moved to ${pendingCampaignChange.toCampaign}. They were logged out — they must reconnect PolicyDial to join the new campaign.`
      )
      setPendingCampaignChange(null)
      loadDashboard(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to change campaign')
    } finally {
      setChangingCampaign(false)
    }
  }, [pendingCampaignChange, loadDashboard])

  const confirmSupervisorAction = useCallback(async () => {
    const pendingAction = pendingSupervisorAction
    if (!pendingAction?.agentUsers.length) return

    setPendingSupervisorAction(null)
    setRunningSupervisorAction(true)

    const action = pendingAction.action
    let successCount = 0
    let warningCount = 0
    const failures: string[] = []

    try {
      for (const agentUser of pendingAction.agentUsers) {
        try {
          if (action === 'logout') {
            const data = await authPost<SupervisorLogoutResponse>(
              `${BACKEND_URL}/api/telephony/supervisor/logout`,
              {
                agent_user: agentUser,
                provider: 'VICIDIAL',
                force_hangup: true,
              }
            )
            if (data.active_session) {
              warningCount += 1
            } else {
              successCount += 1
            }
            continue
          }

          await authPost(`${BACKEND_URL}/api/telephony/supervisor/pause`, {
            agent_user: agentUser,
            provider: 'VICIDIAL',
            paused: action === 'pause',
          })
          successCount += 1
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.message || `${action} failed`
          failures.push(`${agentUser}: ${msg}`)
        }
      }

      if (action === 'logout') {
        if (successCount > 0) {
          toast.success(
            `${successCount} agent${successCount === 1 ? '' : 's'} logged out from PolicyDial.`
          )
        }
        if (warningCount > 0) {
          toast.warning(
            `${warningCount} agent${warningCount === 1 ? '' : 's'} still appear active after logout.`
          )
        }
      } else if (successCount > 0) {
        toast.success(
          `${successCount} agent${successCount === 1 ? '' : 's'} ${
            action === 'pause' ? 'paused' : 'resumed'
          } in PolicyDial.`
        )
      }

      if (failures.length > 0) {
        toast.error(
          failures.length === 1
            ? failures[0]
            : `${failures.length} agents failed during ${action}. First error: ${failures[0]}`
        )
      }
    } finally {
      setRunningSupervisorAction(false)
      setSelectedAgentUsers([])
      await loadDashboard(true)
    }
  }, [loadDashboard, pendingSupervisorAction])

  const agents = useMemo(() => dashboard?.agents ?? [], [dashboard?.agents])
  const agentMap = useMemo(
    () => new Map(agents.map((agent) => [String(agent.user || '').trim(), agent])),
    [agents]
  )
  const liveUserGroups = useMemo(
    () =>
      Array.from(
        new Set(
          agents.map((agent) => String(agent.user_group || '').trim()).filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [agents]
  )
  const liveCampaigns = useMemo(
    () =>
      Array.from(
        new Set(
          agents.map((agent) => String(agent.campaign_id || '').trim()).filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [agents]
  )
  // Campaign options for the Call log filter: live campaigns + any seen in the loaded log.
  const callLogCampaignOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...liveCampaigns,
          ...callLog.map((c) => String(c.campaign || '').trim()).filter(Boolean),
        ])
      ).sort((left, right) => left.localeCompare(right)),
    [liveCampaigns, callLog]
  )
  const agentFilterOptions = useMemo(() => {
    const map = new Map<string, { userId: string; label: string; live: boolean }>()
    supervisorUsers.forEach((entry) => {
      const userId = String(entry.agent_user || '').trim()
      if (!userId) return
      const fullName = String(entry.full_name || '').trim()
      const group = String(entry.user_group || '').trim()
      const liveTag = entry.live ? '● live' : ''
      const label = [userId, fullName, group ? `(${group})` : '', liveTag]
        .filter(Boolean)
        .join(' — ')
      map.set(userId, { userId, label, live: !!entry.live })
    })
    agents.forEach((agent) => {
      const userId = String(agent.user || '').trim()
      if (!userId || map.has(userId)) return
      const fullName = String(agent.full_name || '').trim()
      const group = String(agent.user_group || '').trim()
      const label = [userId, fullName, group ? `(${group})` : '', '● live']
        .filter(Boolean)
        .join(' — ')
      map.set(userId, { userId, label, live: true })
    })
    return Array.from(map.values()).sort((left, right) => {
      if (left.live !== right.live) return left.live ? -1 : 1
      return left.userId.localeCompare(right.userId)
    })
  }, [supervisorUsers, agents])
  const liveAgents = useMemo(
    () =>
      agents.filter((agent) => {
        const groupMatches =
          !liveGroupFilter || String(agent.user_group || '').trim() === liveGroupFilter
        const campaignMatches =
          !liveCampaignFilter || String(agent.campaign_id || '').trim() === liveCampaignFilter
        return groupMatches && campaignMatches
      }),
    [agents, liveCampaignFilter, liveGroupFilter]
  )
  const sortedLiveAgents = useMemo(() => {
    const directionMultiplier = liveAgentsSort.direction === 'asc' ? 1 : -1

    return [...liveAgents].sort((left, right) => {
      let comparison = 0

      switch (liveAgentsSort.key) {
        case 'user':
          comparison = compareSupervisorStrings(left.user, right.user)
          break
        case 'full_name':
          comparison = compareSupervisorStrings(left.full_name, right.full_name)
          break
        case 'campaign_id':
          comparison = compareSupervisorStrings(left.campaign_id, right.campaign_id)
          break
        case 'conf_exten':
          comparison = compareSupervisorStrings(left.conf_exten, right.conf_exten)
          break
        case 'status':
          comparison = compareSupervisorStrings(normalizeStatus(left.status), normalizeStatus(right.status))
          break
        case 'sec_in_state':
          comparison = compareSupervisorNumbers(
            getTimeInStateSeconds(left.user),
            getTimeInStateSeconds(right.user)
          )
          break
        case 'user_group':
          comparison = compareSupervisorStrings(left.user_group, right.user_group)
          break
        case 'calls_today':
          comparison = compareSupervisorNumbers(
            left.policytools_calls_today ?? left.calls_today ?? 0,
            right.policytools_calls_today ?? right.calls_today ?? 0
          )
          break
      }

      if (comparison === 0) {
        return compareSupervisorStrings(left.user, right.user)
      }

      return comparison * directionMultiplier
    })
  }, [getTimeInStateSeconds, liveAgents, liveAgentsSort.direction, liveAgentsSort.key])
  const visibleAgentUsers = useMemo(
    () =>
      Array.from(
        new Set(sortedLiveAgents.map((agent) => String(agent.user || '').trim()).filter(Boolean))
      ),
    [sortedLiveAgents]
  )
  const allVisibleSelected = useMemo(
    () =>
      visibleAgentUsers.length > 0 &&
      visibleAgentUsers.every((agentUser) => selectedAgentUsers.includes(agentUser)),
    [selectedAgentUsers, visibleAgentUsers]
  )
  const totalInCall = useMemo(
    () => liveAgents.filter((a) => normalizeStatus(a.status) === 'INCALL').length,
    [liveAgents]
  )
  const totalWaiting = useMemo(
    () => liveAgents.filter((a) => LIVE_WAITING_STATUSES.has(normalizeStatus(a.status))).length,
    [liveAgents]
  )
  const totalPaused = useMemo(
    () => liveAgents.filter((a) => normalizeStatus(a.status) === 'PAUSED').length,
    [liveAgents]
  )
  const totalInDispo = useMemo(
    () => liveAgents.filter((a) => normalizeStatus(a.status) === 'DISPO').length,
    [liveAgents]
  )
  const totalDeadCalls = useMemo(
    () => liveAgents.filter((a) => normalizeStatus(a.status) === 'DEAD').length,
    [liveAgents]
  )
  const summaryCards = useMemo(
    () => [
      {
        label: 'Agents logged in',
        value: liveAgents.length,
        valueClassName: '',
        icon: 'bi-people-fill',
        iconColor: '#0d6efd',
        iconBackground: 'rgba(13, 110, 253, 0.12)',
        helper: `${liveAgents.length} active PolicyDial sessions`,
      },
      {
        label: 'Agents in calls',
        value: totalInCall,
        valueClassName: 'text-primary',
        icon: 'bi-telephone-fill',
        iconColor: '#0d6efd',
        iconBackground: 'rgba(13, 110, 253, 0.12)',
        helper: 'Live call sessions in progress',
      },
      {
        label: 'Agents waiting',
        value: totalWaiting,
        valueClassName: 'text-success',
        icon: 'bi-check-circle-fill',
        iconColor: '#198754',
        iconBackground: 'rgba(25, 135, 84, 0.14)',
        helper: 'READY and CLOSER states',
      },
      {
        label: 'Paused agents',
        value: totalPaused,
        valueClassName: 'text-warning',
        icon: 'bi-pause-circle-fill',
        iconColor: '#f59f00',
        iconBackground: 'rgba(245, 159, 0, 0.14)',
        helper: 'Agents temporarily unavailable',
      },
      {
        label: 'Agents in dispo',
        value: totalInDispo,
        valueClassName: 'text-secondary',
        icon: 'bi-journal-check',
        iconColor: '#6c757d',
        iconBackground: 'rgba(108, 117, 125, 0.14)',
        helper: 'Wrap-up / disposition work',
      },
      {
        label: 'Dead calls',
        value: totalDeadCalls,
        valueClassName: 'text-danger',
        icon: 'bi-telephone-x-fill',
        iconColor: '#dc3545',
        iconBackground: 'rgba(220, 53, 69, 0.12)',
        helper: 'Sessions marked DEAD in PolicyDial',
      },
    ],
    [liveAgents.length, totalDeadCalls, totalInCall, totalInDispo, totalPaused, totalWaiting]
  )

  useEffect(() => {
    if (!selectedAgentUsers.length) return
    const visibleSet = new Set(visibleAgentUsers)
    setSelectedAgentUsers((current) => current.filter((agentUser) => visibleSet.has(agentUser)))
  }, [selectedAgentUsers.length, visibleAgentUsers])

  const toggleSelectVisibleAgents = useCallback(() => {
    if (runningSupervisorAction) return
    setSelectedAgentUsers((current) => {
      if (allVisibleSelected) {
        const visibleSet = new Set(visibleAgentUsers)
        return current.filter((agentUser) => !visibleSet.has(agentUser))
      }
      return Array.from(new Set([...current, ...visibleAgentUsers]))
    })
  }, [allVisibleSelected, runningSupervisorAction, visibleAgentUsers])

  const toggleSelectAgent = useCallback((agentUser: string) => {
    if (!agentUser || runningSupervisorAction) return
    setSelectedAgentUsers((current) =>
      current.includes(agentUser)
        ? current.filter((existingAgentUser) => existingAgentUser !== agentUser)
        : [...current, agentUser]
    )
  }, [runningSupervisorAction])
  const toggleLiveAgentsSort = useCallback((key: LiveAgentsSortKey) => {
    setLiveAgentsSort((current) =>
      current.key === key
        ? {
            key,
            direction: current.direction === 'asc' ? 'desc' : 'asc',
          }
        : {
            key,
            direction: 'asc',
          }
    )
  }, [])
  const renderLiveAgentsSortHeader = (
    label: string,
    key: LiveAgentsSortKey,
    className?: string
  ) => {
    const active = liveAgentsSort.key === key
    return (
      <th
        className={className}
        aria-sort={
          active ? (liveAgentsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
        }
      >
        <button
          type='button'
          className='btn btn-link btn-sm p-0 text-decoration-none d-inline-flex align-items-center gap-1 fw-semibold text-dark'
          onClick={() => toggleLiveAgentsSort(key)}
        >
          <span>{label}</span>
          <span className={active ? 'text-primary' : 'text-muted'} aria-hidden='true'>
            {active ? (liveAgentsSort.direction === 'asc' ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    )
  }

  const pendingActionType = pendingSupervisorAction?.action || 'logout'
  const pendingActionAgents = pendingSupervisorAction?.agentUsers || []
  const pendingLiveRiskCount = pendingActionAgents.filter((agentUser) => {
    const status = normalizeStatus(agentMap.get(agentUser)?.status || '')
    return status === 'INCALL' || status === 'QUEUE' || status === 'DISPO'
  }).length
  const pendingActionTitle =
    pendingActionType === 'logout'
      ? 'Confirm Force Logout'
      : pendingActionType === 'pause'
        ? 'Confirm Bulk Pause'
        : 'Confirm Bulk Resume'
  const pendingActionPrimaryLabel =
    pendingActionType === 'logout'
      ? 'Force Logout'
      : pendingActionType === 'pause'
        ? 'Pause Selected'
        : 'Resume Selected'
  const pendingActionPrimaryVariant =
    pendingActionType === 'logout'
      ? 'danger'
      : pendingActionType === 'pause'
        ? 'warning'
        : 'success'
  const pendingActionDescription =
    pendingActionType === 'logout'
      ? 'This will disconnect the selected PolicyDial sessions. Any active calls on those sessions may be dropped.'
      : pendingActionType === 'pause'
        ? 'This will set the selected PolicyDial agents to a paused state.'
        : 'This will attempt to return the selected PolicyDial agents to an active state.'
  const pendingActionNoticeStyle: CSSProperties =
    pendingLiveRiskCount > 0 || pendingActionType === 'logout'
      ? {
          backgroundColor: '#FFF7E6',
          border: '1px solid #FCD34D',
          color: '#92400E',
        }
      : {
          backgroundColor: '#F8FAFC',
          border: '1px solid #CBD5E1',
          color: '#334155',
        }

  if (resolved && !hasAccess) {
    return (
      <div className='container-fluid py-4'>
        <Alert variant='warning' className='d-flex align-items-center gap-3'>
          <i className='bi bi-shield-lock fs-3' />
          <div>
            <div className='fw-semibold'>Access restricted</div>
            <div className='text-muted fs-7'>
              Your account does not have access to the PolicyDial supervisor dashboard. Ask an
              administrator to enable it from Users → PolicyDial Settings.
            </div>
          </div>
        </Alert>
      </div>
    )
  }

  return (
    <div className='container-fluid py-4'>
      <div className='d-flex align-items-center mb-4 gap-2'>
        <Button
          variant='light'
          size='sm'
          onClick={() => navigate('/integrations')}
          title='Back to integrations'
        >
          <i className='bi bi-arrow-left me-1' />
          Back
        </Button>
        <h2 className='mb-0 mx-2'>Telephony Supervisor</h2>
        <Button
          variant='light'
          size='sm'
          onClick={() => {
            loadDashboard(false)
            loadMetrics()
          }}
          disabled={loadingDashboard || loadingMetrics}
        >
          <i className='bi bi-arrow-clockwise me-1' />
          Refresh
        </Button>
        <Button
          variant='light'
          size='sm'
          className='ms-auto'
          onClick={() => navigate('/integrations')}
          title='Close'
          aria-label='Close'
        >
          <i className='bi bi-x-lg' />
        </Button>
      </div>

      {activeMonitor && (
        <Alert
          variant='info'
          className='d-flex align-items-center justify-content-between gap-3 flex-wrap mb-4'
        >
          <div>
            <div className='fw-semibold mb-1'>
              <i className='bi bi-headphones me-2' />
              {activeMonitor.stage} active on {activeMonitor.targetAgentName}
              {activeMonitor.targetAgentName !== activeMonitor.targetAgentUser
                ? ` (${activeMonitor.targetAgentUser})`
                : ''}
            </div>
            <div className='small text-muted'>
              Answer the incoming call in the PolicyDial drawer. Click Stop when you are done — it
              hangs up the supervisor leg and restores your previous agent status.
            </div>
          </div>
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={() => void handleStopMonitor()}
            disabled={stoppingMonitor}
          >
            <i className='bi bi-stop-circle me-1' />
            {stoppingMonitor ? 'Stopping...' : 'Stop'}
          </Button>
        </Alert>
      )}

      <Tabs defaultActiveKey='live' className='mb-3'>
        <Tab eventKey='live' title='Live'>
      <Row className='g-3 mb-4'>
        {summaryCards.map((card) => (
          <Col md={6} xl={4} xxl={2} key={card.label}>
            <Card className='shadow-sm border-0 h-100'>
              <Card.Body className='d-flex align-items-start justify-content-between gap-3'>
                <div>
                  <div className='text-muted small text-uppercase fw-semibold'>{card.label}</div>
                  <div className={`fs-2 fw-bold ${card.valueClassName}`.trim()}>{card.value}</div>
                  <div>{card.helper}</div>
                </div>
                <div
                  className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                  style={{
                    width: 56,
                    height: 56,
                    backgroundColor: card.iconBackground,
                    color: card.iconColor,
                  }}
                >
                  <i className={`${card.icon} fs-2`} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <Card className='shadow-sm mb-4'>
        <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
          <div>
            <div className='fw-semibold'>Live PolicyDial</div>
            <div className='text-muted small'>
              Session counts and agent statuses come directly from PolicyDial.
            </div>
          </div>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <Badge bg='light' text='dark' className='border'>
              Global queue calls: {dashboard?.calls_in_queue_count ?? 0}
            </Badge>
            {(() => {
              const good = sortedLiveAgents.filter((a) => a.audio?.quality === 'good').length
              const fair = sortedLiveAgents.filter((a) => a.audio?.quality === 'fair').length
              const poor = sortedLiveAgents.filter((a) => a.audio?.quality === 'poor').length
              if (!good && !fair && !poor) return null
              return (
                <Badge bg='light' text='dark' className='border' title='Live audio quality across agents on a call'>
                  Audio health:{' '}
                  <span className='text-success'>●</span> {good} good{' · '}
                  <span className='text-warning'>●</span> {fair} fair{' · '}
                  <span className='text-danger'>●</span> {poor} poor
                </Badge>
              )
            })()}
            <Form.Select
              size='sm'
              style={{ width: 180 }}
              value={liveGroupFilter}
              onChange={(e) => setLiveGroupFilter(e.target.value)}
            >
              <option value=''>All user groups</option>
              {liveUserGroups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              size='sm'
              style={{ width: 160 }}
              value={liveCampaignFilter}
              onChange={(e) => setLiveCampaignFilter(e.target.value)}
            >
              <option value=''>All campaigns</option>
              {liveCampaigns.map((campaign) => (
                <option key={campaign} value={campaign}>
                  {campaign}
                </option>
              ))}
            </Form.Select>
            <Button
              size='sm'
              variant='light'
              onClick={() => {
                setLiveGroupFilter('')
                setLiveCampaignFilter('')
              }}
              disabled={!liveGroupFilter && !liveCampaignFilter}
            >
              Reset
            </Button>
            {loadingDashboard && <Spinner animation='border' size='sm' />}
          </div>
        </Card.Header>
        <Card.Body className='p-0'>
          {selectedAgentUsers.length > 0 && (
            <div
              className='d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-3 border-bottom'
              style={{ backgroundColor: '#F8FAFC' }}
            >
              <div className='small fw-semibold text-dark'>
                {selectedAgentUsers.length} agent{selectedAgentUsers.length === 1 ? '' : 's'} selected
              </div>
              <ButtonGroup size='sm'>
                <Button
                  variant='outline-warning'
                  disabled={runningSupervisorAction}
                  onClick={() => requestSupervisorAction('pause', selectedAgentUsers)}
                >
                  <i className='bi bi-pause-circle me-1' />
                  Pause Selected
                </Button>
                <Button
                  variant='outline-success'
                  disabled={runningSupervisorAction}
                  onClick={() => requestSupervisorAction('resume', selectedAgentUsers)}
                >
                  <i className='bi bi-play-circle me-1' />
                  Resume Selected
                </Button>
                <Button
                  variant='outline-danger'
                  disabled={runningSupervisorAction}
                  onClick={() => requestSupervisorAction('logout', selectedAgentUsers)}
                >
                  <i className='bi bi-box-arrow-right me-1' />
                  Force Logout
                </Button>
              </ButtonGroup>
            </div>
          )}
          {dashboardError && (
            <Alert variant='danger' className='m-3'>
              {dashboardError}
            </Alert>
          )}
          <Table hover responsive className='mb-0 align-middle'>
            <thead className='table-light'>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={allVisibleSelected}
                    disabled={!visibleAgentUsers.length || runningSupervisorAction}
                    onChange={toggleSelectVisibleAgents}
                    aria-label='Select all visible agents'
                  />
                </th>
                {renderLiveAgentsSortHeader('Agent', 'user')}
                {renderLiveAgentsSortHeader('Name', 'full_name')}
                {renderLiveAgentsSortHeader('Campaign', 'campaign_id')}
                {renderLiveAgentsSortHeader('Session', 'conf_exten')}
                {renderLiveAgentsSortHeader('Status', 'status')}
                <th>Pause reason</th>
                {renderLiveAgentsSortHeader('Time in state', 'sec_in_state', 'text-end')}
                {renderLiveAgentsSortHeader('Group', 'user_group')}
                {renderLiveAgentsSortHeader('Calls today (PolicyDial)', 'calls_today', 'text-end')}
                <th>Audio quality</th>
                <th className='text-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 && !loadingDashboard && (
                <tr>
                  <td colSpan={12} className='text-center text-muted py-4'>
                    No agents currently logged in
                  </td>
                </tr>
              )}
              {agents.length > 0 && liveAgents.length === 0 && !loadingDashboard && (
                <tr>
                  <td colSpan={12} className='text-center text-muted py-4'>
                    No live agents match the current filters
                  </td>
                </tr>
              )}
              {sortedLiveAgents.map((a) => {
                const status = normalizeStatus(a.status)
                const variant = STATUS_VARIANT[status] || 'light'
                const badgeStyle = STATUS_BADGE_STYLE[status] || { backgroundColor: '#F3F4F6', color: '#111827' }
                const rowBusy = monitoringAgent === a.user || runningSupervisorAction
                return (
                  <tr key={`${a.user}-${a.conf_exten}`}>
                    <td>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={selectedAgentUsers.includes(a.user)}
                        disabled={runningSupervisorAction}
                        onChange={() => toggleSelectAgent(a.user)}
                        aria-label={`Select ${a.user}`}
                      />
                    </td>
                    <td className='fw-semibold'>{a.user}</td>
                    <td>{a.full_name || '—'}</td>
                    <td>
                      <Form.Select
                        size='sm'
                        value={a.campaign_id || ''}
                        disabled={rowBusy || changingCampaign}
                        style={{ minWidth: 120 }}
                        title='Change campaign (logs the agent out to re-join)'
                        onChange={(e) => {
                          const to = e.target.value
                          if (to && to !== a.campaign_id) {
                            setPendingCampaignChange({
                              agentUser: a.user,
                              agentName: a.full_name || a.user,
                              fromCampaign: a.campaign_id || '',
                              toCampaign: to,
                            })
                          }
                        }}
                      >
                        {!a.campaign_id && <option value=''>—</option>}
                        {a.campaign_id &&
                          !(dialerHealth?.campaigns || []).some((c) => c.campaign_id === a.campaign_id) && (
                            <option value={a.campaign_id}>{a.campaign_id}</option>
                          )}
                        {(dialerHealth?.campaigns || []).map((c) => (
                          <option key={c.campaign_id} value={c.campaign_id}>
                            {c.campaign_id}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>{a.conf_exten || '—'}</td>
                    <td>
                      <Badge bg={variant} style={badgeStyle} className='border-0'>
                        {status || '—'}
                      </Badge>
                    </td>
                    <td className='small'>
                      {status === 'PAUSED' ? a.pause_code_name || a.pause_code || '—' : '—'}
                    </td>
                    <td className='text-end font-monospace small'>{formatTimeInState(a.user)}</td>
                    <td>{a.user_group || '—'}</td>
                    <td className='text-end'>{a.policytools_calls_today ?? a.calls_today ?? 0}</td>
                    <td className='small'>
                      {a.audio ? (
                        <span
                          title={`Codec ${a.audio.codec} · loss recv ${a.audio.recv_loss_pct}% / send ${a.audio.send_loss_pct}% · jitter ${a.audio.jitter_ms} ms`}
                        >
                          <Badge
                            bg={
                              a.audio.quality === 'good'
                                ? 'success'
                                : a.audio.quality === 'fair'
                                ? 'warning'
                                : 'danger'
                            }
                            className={`border-0 text-capitalize${a.audio.quality === 'fair' ? ' text-dark' : ''}`}
                          >
                            {a.audio.quality}
                          </Badge>{' '}
                          <span className='text-muted'>
                            {Math.max(a.audio.recv_loss_pct, a.audio.send_loss_pct)}% · {a.audio.jitter_ms}ms
                          </span>
                        </span>
                      ) : (
                        <span className='text-muted'>—</span>
                      )}
                      {a.behavior?.flagged && (
                        <div
                          className='text-danger mt-1'
                          style={{ fontSize: '0.7rem', fontWeight: 600 }}
                          title='Network looks clean but calls keep ending in seconds — the agent likely cannot hear the customer (device/output problem). Ask them to run Audio & Device Check.'
                        >
                          <i className='bi bi-exclamation-triangle-fill me-1' />
                          {a.behavior.flag_reason}
                        </div>
                      )}
                    </td>
                    <td className='text-end'>
                      <ButtonGroup size='sm'>
                        <Button
                          variant='outline-info'
                          disabled={status !== 'INCALL' || rowBusy}
                          title='Silent monitor'
                          onClick={() => handleMonitor(a.user, 'MONITOR')}
                        >
                          <i className='bi bi-headphones' /> Monitor
                        </Button>
                        <Button
                          variant='outline-primary'
                          disabled={status !== 'INCALL' || rowBusy}
                          title='Whisper to agent'
                          onClick={() => handleMonitor(a.user, 'WHISPER')}
                        >
                          <i className='bi bi-megaphone' /> Whisper
                        </Button>
                        <Button
                          variant='outline-danger'
                          disabled={status !== 'INCALL' || rowBusy}
                          title='Barge (two-way)'
                          onClick={() => handleMonitor(a.user, 'BARGE')}
                        >
                          <i className='bi bi-telephone-plus' /> Barge
                        </Button>
                        <Button
                          variant='outline-secondary'
                          disabled={rowBusy}
                          title='Force logout from PolicyDial'
                          onClick={() => requestSupervisorAction('logout', [a.user])}
                        >
                          <i className='bi bi-box-arrow-right' />{' '}
                          {runningSupervisorAction ? 'Working...' : 'Logout'}
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
        </Tab>

        <Tab eventKey='metrics' title='Metrics'>
      <Card className='shadow-sm'>
        <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
          <div>
            <div className='fw-semibold'>PolicyTools Metrics</div>
            <div className='text-muted small'>
              Historical analytics based on local call records and webhook sync.
            </div>
          </div>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <Form.Select
              size='sm'
              style={{ width: 140 }}
              value={sinceHours}
              onChange={(e) => setSinceHours(Number(e.target.value))}
              disabled={Boolean(dateFrom || dateTo)}
              title={dateFrom || dateTo ? 'Clear the date range to use a relative window' : undefined}
            >
              <option value={1}>Last 1 hour</option>
              <option value={4}>Last 4 hours</option>
              <option value={8}>Last 8 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
            </Form.Select>
            <Form.Control
              type='date'
              size='sm'
              style={{ width: 150 }}
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              title='From date'
            />
            <span className='text-muted small'>→</span>
            <Form.Control
              type='date'
              size='sm'
              style={{ width: 150 }}
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              title='To date'
            />
            {(dateFrom || dateTo) && (
              <Button
                size='sm'
                variant='light'
                onClick={() => {
                  setDateFrom('')
                  setDateTo('')
                }}
                title='Clear date range'
              >
                Clear dates
              </Button>
            )}
            <Form.Select
              size='sm'
              style={{ width: 160 }}
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
            >
              <option value=''>All campaigns</option>
              {liveCampaigns.map((campaignId) => (
                <option key={campaignId} value={campaignId}>
                  {campaignId}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              size='sm'
              style={{ width: 240 }}
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
            >
              <option value=''>All agents</option>
              {agentFilterOptions.map((option) => (
                <option key={option.userId} value={option.userId}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Button size='sm' onClick={loadMetrics} disabled={loadingMetrics}>
              {loadingMetrics ? <Spinner animation='border' size='sm' /> : 'Apply'}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {metricsError && <Alert variant='danger'>{metricsError}</Alert>}

          {(() => {
            const totalCalls = metrics?.total_calls ?? 0
            const realConvs = metrics?.real_conversations ?? 0
            const dispositioned = metrics?.dispositioned ?? 0
            const sales = metrics?.sales ?? 0
            const callbacks = metrics?.callbacks ?? 0
            const realConvRate = totalCalls ? realConvs / totalCalls : 0
            const dispoOfConvs = realConvs ? dispositioned / realConvs : 0
            const saleOfDispos = dispositioned ? sales / dispositioned : 0
            const saleRate = totalCalls ? sales / totalCalls : 0
            const callbackRate = totalCalls ? callbacks / totalCalls : 0
            const convThreshold = metrics?.real_conversation_threshold_seconds ?? 30

            const FunnelStep = ({
              label,
              value,
              subtext,
              bg,
            }: {
              label: string
              value: number | string
              subtext?: string
              bg: string
            }) => (
              <div
                className='rounded text-center px-3 py-3 h-100 d-flex flex-column justify-content-center'
                style={{ background: bg, color: 'white', minHeight: 110 }}
              >
                <div className='small text-uppercase opacity-75' style={{ letterSpacing: 0.5 }}>
                  {label}
                </div>
                <div className='fs-3 fw-bold lh-1 my-1'>{value}</div>
                {subtext && <div className='small opacity-75'>{subtext}</div>}
              </div>
            )

            return (
              <>
                <Row className='g-2 mb-4 align-items-stretch'>
                  <Col xs={6} md>
                    <FunnelStep label='Calls' value={totalCalls} subtext='100%' bg='#1f4068' />
                  </Col>
                  <Col xs={6} md>
                    <FunnelStep
                      label={`Conversations (>${convThreshold}s)`}
                      value={realConvs}
                      subtext={`${formatPercent(realConvRate)} of calls`}
                      bg='#2e6fa7'
                    />
                  </Col>
                  <Col xs={6} md>
                    <FunnelStep
                      label='Dispositioned'
                      value={dispositioned}
                      subtext={`${formatPercent(dispoOfConvs)} of conv.`}
                      bg='#3d8dd0'
                    />
                  </Col>
                  <Col xs={6} md>
                    <FunnelStep
                      label='Sales'
                      value={sales}
                      subtext={`${formatPercent(saleOfDispos)} of dispo`}
                      bg='#1ea672'
                    />
                  </Col>
                </Row>

                <Row className='g-3 mb-4'>
                  <Col xs={6} md={3}>
                    <div className='text-muted small'>Sale rate</div>
                    <div className='fs-4 fw-bold text-success'>{formatPercent(saleRate)}</div>
                    <div className='text-muted small'>
                      {sales} / {totalCalls}
                    </div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className='text-muted small'>Callback rate</div>
                    <div className='fs-4 fw-bold'>{formatPercent(callbackRate)}</div>
                    <div className='text-muted small'>
                      {callbacks} / {totalCalls}
                    </div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className='text-muted small'>Avg talk time</div>
                    <div className='fs-4 fw-bold'>
                      {formatSeconds(metrics?.avg_talk_time_seconds ?? 0)}
                    </div>
                    <div className='text-muted small'>on real conversations</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className='text-muted small'>Calls / hour</div>
                    <div className='fs-4 fw-bold'>
                      {(metrics?.calls_per_hour ?? 0).toFixed(1)}
                    </div>
                  </Col>
                </Row>
              </>
            )
          })()}

          {(metrics?.per_day || []).length > 0 && (
            <>
              <h6 className='fw-semibold mb-2'>Conversations per day</h6>
              <Table size='sm' responsive hover className='mb-4'>
                <thead className='table-light'>
                  <tr>
                    <th>Day</th>
                    <th className='text-end'>Conversations</th>
                    <th className='text-end'>Total time</th>
                    <th className='text-end'>Avg / call</th>
                    <th className='text-end'>Calls</th>
                  </tr>
                </thead>
                <tbody>
                  {(metrics?.per_day || []).map((row) => (
                    <tr key={row.day}>
                      <td className='fw-semibold'>{formatDayLabel(row.day)}</td>
                      <td className='text-end'>{row.real_conversations}</td>
                      <td className='text-end'>{formatDuration(row.total_talk_seconds)}</td>
                      <td className='text-end'>{formatSeconds(row.avg_talk_time_seconds)}</td>
                      <td className='text-end text-muted'>{row.total_calls}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className='fw-semibold border-top'>
                    <td>Total</td>
                    <td className='text-end'>
                      {(metrics?.per_day || []).reduce((sum, r) => sum + r.real_conversations, 0)}
                    </td>
                    <td className='text-end'>
                      {formatDuration(
                        (metrics?.per_day || []).reduce((sum, r) => sum + r.total_talk_seconds, 0)
                      )}
                    </td>
                    <td className='text-end'>—</td>
                    <td className='text-end text-muted'>
                      {(metrics?.per_day || []).reduce((sum, r) => sum + r.total_calls, 0)}
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </>
          )}

          <Row>
            <Col md={6}>
              <h6 className='fw-semibold mb-2'>By agent</h6>
              <Table size='sm' responsive hover>
                <thead className='table-light'>
                  <tr>
                    <th>Agent</th>
                    <th className='text-end'>Calls</th>
                    <th className='text-end'>Conv.</th>
                    <th className='text-end'>Sales</th>
                    <th className='text-end'>Sale %</th>
                    <th className='text-end'>Avg talk</th>
                    <th className='text-end'>C/h</th>
                  </tr>
                </thead>
                <tbody>
                  {(metrics?.per_agent || []).length === 0 && (
                    <tr>
                      <td colSpan={7} className='text-center text-muted py-3'>
                        No data
                      </td>
                    </tr>
                  )}
                  {(metrics?.per_agent || []).map((row) => (
                    <tr key={row.agent_user}>
                      <td>{row.agent_user}</td>
                      <td className='text-end'>{row.total_calls}</td>
                      <td className='text-end'>{row.real_conversations ?? 0}</td>
                      <td className='text-end'>{row.sales ?? 0}</td>
                      <td className='text-end'>{formatPercent(row.sale_rate ?? 0)}</td>
                      <td className='text-end'>{formatSeconds(row.avg_talk_time_seconds)}</td>
                      <td className='text-end'>{row.calls_per_hour.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <h6 className='fw-semibold mb-2'>By campaign</h6>
              <Table size='sm' responsive hover>
                <thead className='table-light'>
                  <tr>
                    <th>Campaign</th>
                    <th className='text-end'>Calls</th>
                    <th className='text-end'>Conv.</th>
                    <th className='text-end'>Sales</th>
                    <th className='text-end'>Sale %</th>
                    <th className='text-end'>Avg talk</th>
                    <th className='text-end'>C/h</th>
                  </tr>
                </thead>
                <tbody>
                  {(metrics?.per_campaign || []).length === 0 && (
                    <tr>
                      <td colSpan={7} className='text-center text-muted py-3'>
                        No data
                      </td>
                    </tr>
                  )}
                  {(metrics?.per_campaign || []).map((row) => (
                    <tr key={row.campaign_id}>
                      <td>{row.campaign_id}</td>
                      <td className='text-end'>{row.total_calls}</td>
                      <td className='text-end'>{row.real_conversations ?? 0}</td>
                      <td className='text-end'>{row.sales ?? 0}</td>
                      <td className='text-end'>{formatPercent(row.sale_rate ?? 0)}</td>
                      <td className='text-end'>{formatSeconds(row.avg_talk_time_seconds)}</td>
                      <td className='text-end'>{row.calls_per_hour.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>

          {(metrics?.dispositions || []).length > 0 && (
            <>
              <h6 className='fw-semibold mb-2 mt-3'>Dispositions</h6>
              <div className='d-flex flex-wrap gap-2'>
                {metrics!.dispositions.map((row) => (
                  <Badge key={row.disposition} bg='light' text='dark' className='border'>
                    {row.disposition}: {row.count}
                  </Badge>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
        </Tab>

        <Tab eventKey='calllog' title='Call log'>
      <Card className='shadow-sm mt-4'>
        <Card.Header className='d-flex flex-column align-items-stretch gap-2'>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
            <div>
              <div className='fw-semibold'>Call log (recordings)</div>
              <div className='text-muted small'>
                Detailed calls straight from PolicyDial (real agent + duration). Press play to listen.
              </div>
            </div>
            {loadingCallLog && <Spinner animation='border' size='sm' />}
          </div>
          <div className='d-flex flex-wrap align-items-end gap-2'>
            <div>
              <label className='text-muted small d-block mb-1'>From</label>
              <Form.Control
                type='date'
                size='sm'
                style={{ width: 150 }}
                value={clDateFrom}
                max={clDateTo || undefined}
                onChange={(e) => setClDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className='text-muted small d-block mb-1'>To</label>
              <Form.Control
                type='date'
                size='sm'
                style={{ width: 150 }}
                value={clDateTo}
                min={clDateFrom || undefined}
                onChange={(e) => setClDateTo(e.target.value)}
              />
            </div>
            <div>
              <label className='text-muted small d-block mb-1'>Agent</label>
              <Form.Select size='sm' style={{ width: 200 }} value={clAgent} onChange={(e) => setClAgent(e.target.value)}>
                <option value=''>All agents</option>
                {callLogAgents.map((a) => (
                  <option key={a.user} value={a.user}>
                    {a.user}
                    {a.full_name ? ` — ${a.full_name}` : ''}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div>
              <label className='text-muted small d-block mb-1'>Phone</label>
              <Form.Control
                size='sm'
                style={{ width: 140 }}
                placeholder='Phone'
                value={clPhone}
                onChange={(e) => setClPhone(e.target.value)}
              />
            </div>
            <div>
              <label className='text-muted small d-block mb-1'>Customer</label>
              <Form.Control
                size='sm'
                style={{ width: 160 }}
                placeholder='Customer name'
                value={clCustomer}
                onChange={(e) => setClCustomer(e.target.value)}
              />
            </div>
            <div>
              <label className='text-muted small d-block mb-1'>Campaign</label>
              <Form.Select size='sm' style={{ width: 150 }} value={clCampaign} onChange={(e) => setClCampaign(e.target.value)}>
                <option value=''>All campaigns</option>
                {callLogCampaignOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div>
              <label className='text-muted small d-block mb-1'>Result</label>
              <Form.Select size='sm' style={{ width: 130 }} value={clStatus} onChange={(e) => setClStatus(e.target.value)}>
                <option value=''>All results</option>
                {CALL_LOG_RESULTS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Form.Select>
            </div>
            <Button size='sm' onClick={() => loadCallLog(0)} disabled={loadingCallLog}>
              {loadingCallLog ? <Spinner animation='border' size='sm' /> : 'Apply'}
            </Button>
            <Button
              size='sm'
              variant='light'
              onClick={() => {
                setClDateFrom('')
                setClDateTo('')
                setClAgent('')
                setClPhone('')
                setClCustomer('')
                setClCampaign('')
                setClStatus('')
              }}
              disabled={loadingCallLog}
            >
              Clear
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {callLogError && <Alert variant='danger'>{callLogError}</Alert>}
          <Table size='sm' responsive hover className='align-middle'>
            <thead className='table-light'>
              <tr>
                <th>Date / time</th>
                <th>Agent</th>
                <th>Campaign</th>
                <th>Phone</th>
                <th>Customer</th>
                <th className='text-end'>Duration</th>
                <th>Result</th>
                <th style={{ minWidth: 240 }}>Recording</th>
              </tr>
            </thead>
            <tbody>
              {callLog.length === 0 && !loadingCallLog && (
                <tr>
                  <td colSpan={8} className='text-center text-muted py-4'>
                    No calls for the selected filters
                  </td>
                </tr>
              )}
              {callLog.map((call) => (
                <tr key={call.uniqueid || `${call.start_time}-${call.recording_id}`}>
                  <td className='font-monospace small'>{call.start_time}</td>
                  <td className='fw-semibold'>{call.agent || '—'}</td>
                  <td>{call.campaign || '—'}</td>
                  <td>{call.phone || '—'}</td>
                  <td>
                    {(() => {
                      const name =
                        [call.first_name, call.last_name].filter(Boolean).join(' ').trim() || '—'
                      const note = (call.lead_comments || '').trim()
                      if (!note) return name
                      return (
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip id={`note-${call.uniqueid}`}>{note}</Tooltip>}
                        >
                          <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                            {name}
                          </span>
                        </OverlayTrigger>
                      )
                    })()}
                  </td>
                  <td className='text-end'>{call.duration < 0 ? '—' : formatSeconds(call.duration)}</td>
                  <td>
                    {call.term_reason === 'AGENT' ? (
                      // Agent set a real disposition (NINT / NANS / SALE ...). Color by
                      // meaning, with soft/readable tones instead of the harsh purple.
                      <Badge
                        bg={
                          ['SALE', 'NCSE', 'INST'].includes(call.status)
                            ? 'success'
                            : call.status === 'NODISP'
                            ? 'secondary'
                            : ['DNC', 'DROP', 'PDROP'].includes(call.status)
                            ? 'warning'
                            : 'light'
                        }
                        text={
                          ['SALE', 'NCSE', 'INST'].includes(call.status) || call.status === 'NODISP'
                            ? undefined
                            : 'dark'
                        }
                        className='border'
                      >
                        {call.status || 'Dispositioned'}
                      </Badge>
                    ) : (
                      // Reached the agent (a recording exists) but vicidial_log kept
                      // the dialer-stage status (QUEUE/DROP) — show it was handled,
                      // with the raw dialer status as a secondary note.
                      <>
                        <Badge bg='success' className='border-0'>Handled</Badge>
                        {call.status && (
                          <span className='text-muted small ms-1'>({call.status})</span>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    {call.recording_url ? (
                      <audio controls preload='none' src={call.recording_url} style={{ height: 32, maxWidth: 240 }} />
                    ) : (
                      <span className='text-muted small'>No recording</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {callLogTotal > 0 && (
            <div className='d-flex justify-content-between align-items-center mt-2'>
              <div className='text-muted small'>
                {callLogOffset + 1}–{Math.min(callLogOffset + CALL_LOG_LIMIT, callLogTotal)} of {callLogTotal}
              </div>
              <ButtonGroup size='sm'>
                <Button
                  variant='light'
                  disabled={loadingCallLog || callLogOffset === 0}
                  onClick={() => loadCallLog(Math.max(0, callLogOffset - CALL_LOG_LIMIT))}
                >
                  ← Prev
                </Button>
                <Button
                  variant='light'
                  disabled={loadingCallLog || callLogOffset + CALL_LOG_LIMIT >= callLogTotal}
                  onClick={() => loadCallLog(callLogOffset + CALL_LOG_LIMIT)}
                >
                  Next →
                </Button>
              </ButtonGroup>
            </div>
          )}
        </Card.Body>
      </Card>
        </Tab>

        <Tab eventKey='dialer' title='Dialer health'>
      <Card className='shadow-sm mb-4'>
        <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
          <div>
            <span className='fw-bold fs-5'>Dialer health</span>
            <div className='text-muted small'>
              Real-time campaign / dialer status straight from PolicyDial.
              {dialerHealth?.as_of ? ` As of ${dialerHealth.as_of}.` : ''}
            </div>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <Form.Select
              size='sm'
              style={{ width: 'auto' }}
              value={dialerCampaign}
              onChange={(e) => setDialerCampaign(e.target.value)}
            >
              <option value=''>All campaigns</option>
              {(dialerHealth?.campaigns || []).map((c) => (
                <option key={c.campaign_id} value={c.campaign_id}>
                  {c.campaign_id} — {c.campaign_name}
                </option>
              ))}
            </Form.Select>
            <Button size='sm' variant='light' onClick={() => loadDialerHealth(false)} disabled={loadingDialer}>
              {loadingDialer ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {dialerError && <Alert variant='danger'>{dialerError}</Alert>}
          {!dialerError && (!dialerHealth || dialerHealth.campaigns.length === 0) && (
            <div className='text-muted text-center py-4'>
              {loadingDialer ? 'Loading…' : 'No active campaigns.'}
            </div>
          )}
          <Row className='g-3'>
            {(dialerHealth?.campaigns || [])
              .filter((c) => !dialerCampaign || c.campaign_id === dialerCampaign)
              .map((c) => {
                const overTarget = c.drop_pct > c.target_drop_pct
                const dropVariant =
                  c.drop_pct <= c.target_drop_pct ? 'success' : c.drop_pct <= c.target_drop_pct * 2 ? 'warning' : 'danger'
                return (
                  <Col key={c.campaign_id} xs={12}>
                    <Card className='border h-100'>
                      <Card.Header className='d-flex justify-content-between align-items-center'>
                        <div>
                          <span className='fw-bold'>{c.campaign_id}</span>
                          <span className='text-muted ms-2 small'>{c.campaign_name}</span>
                        </div>
                        <Badge bg='light' text='dark' className='border'>{c.dial_method}</Badge>
                      </Card.Header>
                      <Card.Body>
                        {/* Headline KPIs — spread across the full width */}
                        <div className='d-flex flex-wrap align-items-center justify-content-between text-center gap-4'>
                          <div>
                            <div className='text-muted small mb-1'>Drop %</div>
                            <Badge bg={dropVariant} className='fs-6'>{c.drop_pct}%</Badge>
                            <div className='text-muted' style={{ fontSize: '0.7rem' }}>target {c.target_drop_pct}%</div>
                          </div>
                          <div>
                            <div className='text-muted small mb-1'>Dial level</div>
                            <div className='fw-bold fs-2'>{c.dial_level}</div>
                          </div>
                          <div>
                            <div className='text-muted small mb-1'>Avg agents</div>
                            <div className='fw-bold fs-2'>{c.avg_agents}</div>
                          </div>
                          <div>
                            <div className='text-muted small mb-1'>Dialable leads</div>
                            <div className='fw-bold fs-2'>{c.dialable_leads.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className='text-muted small mb-1'>Leads in hopper</div>
                            <div className='fw-bold fs-2'>{c.hopper}</div>
                          </div>
                          <div>
                            <div className='text-muted small mb-1'>Agents</div>
                            <div className='fw-bold fs-4'>
                              <span className='text-success'>{c.agents.ready}</span>
                              <span className='text-muted'> / </span>
                              <span className='text-primary'>{c.agents.oncall}</span>
                              <span className='text-muted'> / </span>
                              <span>{c.agents.paused}</span>
                            </div>
                            <div className='text-muted' style={{ fontSize: '0.7rem' }}>ready / on call / paused</div>
                          </div>
                        </div>

                        {/* Today's call breakdown */}
                        <div className='d-flex flex-wrap align-items-center justify-content-between text-center gap-4 border-top mt-3 pt-3'>
                          {[
                            { label: 'Calls today', value: c.calls_today },
                            { label: 'Answers', value: c.answers_today },
                            { label: 'Agent calls', value: c.agent_calls_today },
                            { label: 'Machines', value: c.machines_today },
                            { label: 'Drops', value: c.drops_today },
                            { label: 'NODISP', value: c.nodisp_today },
                          ].map((s) => (
                            <div key={s.label}>
                              <div className='fw-bold fs-4'>{s.value}</div>
                              <div className='text-muted small'>{s.label}</div>
                            </div>
                          ))}
                        </div>

                        {/* Dialer settings strip (mirrors the VICIdial real-time header) */}
                        <div className='d-flex flex-wrap align-items-center gap-2 gap-md-4 border-top mt-3 pt-3 small'>
                          {[
                            { label: 'Dial method', value: c.dial_method },
                            { label: 'Statuses', value: c.statuses },
                            { label: 'Order', value: c.lead_order },
                            { label: 'Hopper (min)', value: c.hopper_level },
                            { label: 'DL diff', value: c.dl_diff },
                            { label: 'Drop sec', value: `${c.drop_call_seconds}s` },
                            { label: 'Trunk short/fill', value: `${c.trunk_short} / ${c.trunk_fill}` },
                          ].map((s) => (
                            <span key={s.label}>
                              <span className='text-muted'>{s.label}: </span>
                              <span className='fw-semibold'>{s.value}</span>
                            </span>
                          ))}
                        </div>
                        {overTarget && (
                          <div className='text-danger small mt-3'>
                            Drop % above target — dialer may be throttling.
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                )
              })}
          </Row>
        </Card.Body>
      </Card>
        </Tab>

        <Tab eventKey='reports' title='Reports'>
      {reportView === '' ? (
        <Card className='shadow-sm mb-4'>
          <Card.Header>
            <span className='fw-bold fs-5'>Reports</span>
            <div className='text-muted small'>PolicyDial reports — click one to open it.</div>
          </Card.Header>
          <Card.Body>
            <Row className='g-3'>
              {REPORT_MENU.map((r) => (
                <Col md={6} lg={4} key={r.key}>
                  <Card
                    className='border h-100'
                    role={r.available ? 'button' : undefined}
                    style={{ cursor: r.available ? 'pointer' : 'default', opacity: r.available ? 1 : 0.6 }}
                    onClick={() => r.available && setReportView(r.key)}
                  >
                    <Card.Body>
                      <div className='d-flex align-items-center gap-2 mb-1'>
                        <i className={`${r.icon} fs-4 text-primary`} />
                        <span className='fw-semibold'>{r.title}</span>
                        {!r.available && <Badge bg='light' text='dark' className='border ms-auto'>Soon</Badge>}
                      </div>
                      <div className='text-muted small'>{r.desc}</div>
                      <div className='text-muted mt-1' style={{ fontSize: '0.7rem' }}>{r.group}</div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Button variant='link' className='px-0 mb-2' onClick={() => setReportView('')}>
            ← Back to reports
          </Button>

          {reportView === 'agentperf' && (
      <Card className='shadow-sm mb-4'>
        <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
          <div>
            <span className='fw-bold fs-5'>Agent performance</span>
            <div className='text-muted small'>
              Real per-agent activity from PolicyDial (talk time from recordings; dispositions as entered).
              {agentPerf ? ` ${agentPerf.date_from} → ${agentPerf.date_to}.` : ''}
            </div>
          </div>
          <div className='d-flex align-items-end gap-2 flex-wrap'>
            <div>
              <Form.Label className='small text-muted mb-0'>Campaign</Form.Label>
              <Form.Select size='sm' style={{ width: 'auto' }} value={perfCampaign} onChange={(e) => setPerfCampaign(e.target.value)}>
                <option value=''>All campaigns</option>
                {(dialerHealth?.campaigns || []).map((c) => (
                  <option key={c.campaign_id} value={c.campaign_id}>{c.campaign_id}</option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Form.Label className='small text-muted mb-0'>User group</Form.Label>
              <Form.Select size='sm' style={{ width: 'auto' }} value={perfGroup} onChange={(e) => setPerfGroup(e.target.value)}>
                <option value=''>All groups</option>
                {(agentPerf?.user_groups || []).map((g) => (
                  <option key={g.user_group} value={g.user_group}>{g.user_group}</option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Form.Label className='small text-muted mb-0'>Agent</Form.Label>
              <Form.Select size='sm' style={{ width: 'auto' }} value={perfAgent} onChange={(e) => setPerfAgent(e.target.value)}>
                <option value=''>All agents</option>
                {callLogAgents.map((a) => (
                  <option key={a.user} value={a.user}>{a.full_name || a.user}</option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Form.Label className='small text-muted mb-0'>From</Form.Label>
              <Form.Control size='sm' type='date' value={perfDateFrom} onChange={(e) => setPerfDateFrom(e.target.value)} />
            </div>
            <div>
              <Form.Label className='small text-muted mb-0'>To</Form.Label>
              <Form.Control size='sm' type='date' value={perfDateTo} onChange={(e) => setPerfDateTo(e.target.value)} />
            </div>
            <Button size='sm' onClick={() => loadAgentPerformance()} disabled={loadingPerf}>
              {loadingPerf ? 'Loading…' : 'Apply'}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {perfError && <Alert variant='danger'>{perfError}</Alert>}
          {!perfError && (!agentPerf || agentPerf.agents.length === 0) && (
            <div className='text-muted text-center py-4'>
              {loadingPerf ? 'Loading…' : 'No agent activity in this range.'}
            </div>
          )}
          {agentPerf && agentPerf.agents.length > 0 && (
            <Table size='sm' responsive hover className='align-middle'>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th className='text-end'>Calls</th>
                  <th className='text-end'>Real convos</th>
                  <th className='text-end'>Talk time</th>
                  <th className='text-end'>Avg talk</th>
                  <th className='text-end'>Sales</th>
                  <th className='text-end'>Disposed</th>
                  <th className='text-end'>Pause time</th>
                  <th>Pause reasons</th>
                  <th>Dispositions</th>
                </tr>
              </thead>
              <tbody>
                {agentPerf.agents.map((a) => {
                  const nodisp = a.dispositions['NODISP'] || 0
                  const disposed = a.calls - nodisp
                  const disposedPct = a.calls > 0 ? Math.round((disposed / a.calls) * 100) : 0
                  return (
                    <tr key={a.user}>
                      <td>
                        <div className='fw-semibold'>{a.full_name || a.user}</div>
                        <div className='text-muted small'>{a.user}</div>
                      </td>
                      <td className='text-end'>{a.calls}</td>
                      <td className='text-end'>{a.real_convos}</td>
                      <td className='text-end'>{formatSeconds(a.talk_sec)}</td>
                      <td className='text-end'>{formatSeconds(a.avg_talk_sec)}</td>
                      <td className='text-end'>
                        {a.sales > 0 ? <Badge bg='success'>{a.sales}</Badge> : <span className='text-muted'>0</span>}
                      </td>
                      <td className='text-end'>
                        <span className={disposedPct < 50 ? 'text-danger fw-semibold' : 'text-success'}>
                          {disposedPct}%
                        </span>
                      </td>
                      <td className='text-end'>{formatSeconds(a.pause_sec_total || 0)}</td>
                      <td>
                        <div className='d-flex flex-wrap gap-1'>
                          {Object.entries(a.pause_by_reason || {})
                            .sort((x, y) => y[1].sec - x[1].sec)
                            .map(([code, info]) => (
                              <Badge key={code} bg='light' text='dark' className='border'>
                                {agentPerf.pause_code_labels?.[code] || code} {formatSeconds(info.sec)}
                              </Badge>
                            ))}
                          {(!a.pause_by_reason || Object.keys(a.pause_by_reason).length === 0) && (
                            <span className='text-muted small'>—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-wrap gap-1'>
                          {Object.entries(a.dispositions)
                            .sort((x, y) => y[1] - x[1])
                            .map(([st, n]) => (
                              <Badge
                                key={st}
                                bg={st === 'NODISP' ? 'warning' : agentPerf.sale_dispositions.includes(st) ? 'success' : 'light'}
                                text={st === 'NODISP' || !agentPerf.sale_dispositions.includes(st) ? 'dark' : undefined}
                                className='border'
                              >
                                {st} {n}
                              </Badge>
                            ))}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
          <div className='text-muted small mt-2'>
            <Badge bg='warning' text='dark' className='border me-1'>NODISP</Badge>
            = connected but the agent entered no disposition. A low "Disposed %" means the agent isn't dispositioning their calls.
          </div>
        </Card.Body>
      </Card>
          )}

          {reportView === 'outbound' && (
            <Card className='shadow-sm mb-4'>
              <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                <div>
                  <span className='fw-bold fs-5'>Outbound Calling Report</span>
                  <div className='text-muted small'>
                    Outbound call totals from PolicyDial.{outbound ? ` ${outbound.date_from} → ${outbound.date_to}.` : ''}
                  </div>
                </div>
                <div className='d-flex align-items-end gap-2 flex-wrap'>
                  <div>
                    <Form.Label className='small text-muted mb-0'>Campaign</Form.Label>
                    <Form.Select size='sm' style={{ width: 'auto' }} value={obCampaign} onChange={(e) => setObCampaign(e.target.value)}>
                      <option value=''>All campaigns</option>
                      {(dialerHealth?.campaigns || []).map((c) => (
                        <option key={c.campaign_id} value={c.campaign_id}>{c.campaign_id}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div>
                    <Form.Label className='small text-muted mb-0'>From</Form.Label>
                    <Form.Control size='sm' type='date' value={obDateFrom} onChange={(e) => setObDateFrom(e.target.value)} />
                  </div>
                  <div>
                    <Form.Label className='small text-muted mb-0'>To</Form.Label>
                    <Form.Control size='sm' type='date' value={obDateTo} onChange={(e) => setObDateTo(e.target.value)} />
                  </div>
                  <Button size='sm' onClick={() => loadOutboundReport()} disabled={loadingOutbound}>
                    {loadingOutbound ? 'Loading…' : 'Apply'}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {outboundError && <Alert variant='danger'>{outboundError}</Alert>}
                {!outboundError && !outbound && (
                  <div className='text-muted text-center py-4'>{loadingOutbound ? 'Loading…' : 'No data.'}</div>
                )}
                {outbound && (
                  <>
                    <div className='d-flex flex-wrap align-items-center justify-content-between text-center gap-4 mb-2'>
                      {[
                        { label: 'Total calls', value: outbound.total_calls },
                        { label: 'Avg length', value: `${outbound.avg_call_len}s` },
                        { label: 'Human answers', value: outbound.human_answers },
                        { label: 'Avg talk (HA)', value: `${outbound.ha_avg_len}s` },
                        { label: 'Machines', value: outbound.machines },
                        { label: 'Sales', value: outbound.sales },
                        { label: 'NODISP', value: outbound.nodisp },
                      ].map((s) => (
                        <div key={s.label}>
                          <div className='fw-bold fs-3'>{s.value}</div>
                          <div className='text-muted small'>{s.label}</div>
                        </div>
                      ))}
                      <div>
                        <div className='fw-bold fs-3'>
                          {outbound.drops}{' '}
                          <span className={outbound.drop_pct_answers > 3 ? 'text-danger fs-6' : 'text-success fs-6'}>
                            ({outbound.drop_pct_answers}%)
                          </span>
                        </div>
                        <div className='text-muted small'>Drops (of answers)</div>
                      </div>
                    </div>
                    <Row className='g-4 mt-1'>
                      <Col md={6}>
                        <div className='fw-semibold mb-2 border-top pt-2'>Hangup reasons</div>
                        <Table size='sm' className='mb-0'>
                          <tbody>
                            {outbound.hangup_reasons.map((h) => (
                              <tr key={h.reason}>
                                <td className='text-muted'>{h.reason}</td>
                                <td className='text-end fw-semibold'>{h.calls}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                      <Col md={6}>
                        <div className='fw-semibold mb-2 border-top pt-2'>Dispositions</div>
                        <Table size='sm' className='mb-0'>
                          <tbody>
                            {outbound.dispositions.map((d) => (
                              <tr key={d.status}>
                                <td className='text-muted'>{d.status}</td>
                                <td className='text-end fw-semibold'>{d.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {reportView === 'lists' && (
            <Card className='shadow-sm mb-4'>
              <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                <div>
                  <span className='fw-bold fs-5'>Lists / Lead Source</span>
                  <div className='text-muted small'>
                    Contact and sale performance per list.{listsReport ? ` ${listsReport.date_from} → ${listsReport.date_to}.` : ''}
                  </div>
                </div>
                <div className='d-flex align-items-end gap-2 flex-wrap'>
                  <div>
                    <Form.Label className='small text-muted mb-0'>Campaign</Form.Label>
                    <Form.Select size='sm' style={{ width: 'auto' }} value={listsCampaign} onChange={(e) => setListsCampaign(e.target.value)}>
                      <option value=''>All campaigns</option>
                      {(dialerHealth?.campaigns || []).map((c) => (
                        <option key={c.campaign_id} value={c.campaign_id}>{c.campaign_id}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div>
                    <Form.Label className='small text-muted mb-0'>From</Form.Label>
                    <Form.Control size='sm' type='date' value={listsDateFrom} onChange={(e) => setListsDateFrom(e.target.value)} />
                  </div>
                  <div>
                    <Form.Label className='small text-muted mb-0'>To</Form.Label>
                    <Form.Control size='sm' type='date' value={listsDateTo} onChange={(e) => setListsDateTo(e.target.value)} />
                  </div>
                  <Button size='sm' onClick={() => loadListsReport()} disabled={loadingLists}>
                    {loadingLists ? 'Loading…' : 'Apply'}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {listsError && <Alert variant='danger'>{listsError}</Alert>}
                {!listsError && (!listsReport || listsReport.lists.length === 0) && (
                  <div className='text-muted text-center py-4'>{loadingLists ? 'Loading…' : 'No list activity in this range.'}</div>
                )}
                {listsReport && listsReport.lists.length > 0 && (
                  <Table size='sm' responsive hover className='align-middle'>
                    <thead>
                      <tr>
                        <th>List</th>
                        <th>Campaign</th>
                        <th className='text-end'>Calls</th>
                        <th className='text-end'>Contacts</th>
                        <th className='text-end'>Contact %</th>
                        <th className='text-end'>Sales</th>
                        <th className='text-end'>Sale %</th>
                        <th className='text-end'>NODISP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listsReport.lists.map((l) => (
                        <tr key={l.list_id}>
                          <td>
                            <div className='fw-semibold'>{l.list_name || l.list_id}</div>
                            <div className='text-muted small'>{l.list_id}</div>
                          </td>
                          <td>{l.campaign_id}</td>
                          <td className='text-end'>{l.calls}</td>
                          <td className='text-end'>{l.contacts}</td>
                          <td className='text-end'>{l.contact_rate}%</td>
                          <td className='text-end'>{l.sales > 0 ? <Badge bg='success'>{l.sales}</Badge> : <span className='text-muted'>0</span>}</td>
                          <td className='text-end'>{l.sale_rate}%</td>
                          <td className='text-end text-muted'>{l.nodisp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}

          {reportView === 'inbound' && (
            <Card className='shadow-sm mb-4'>
              <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                <div>
                  <span className='fw-bold fs-5'>Inbound Report</span>
                  <div className='text-muted small'>
                    Inbound volume & service level{inboundReport ? ` (SL = answered within ${inboundReport.sl_seconds}s). ${inboundReport.date_from} → ${inboundReport.date_to}.` : ''}
                  </div>
                </div>
                <div className='d-flex align-items-end gap-2 flex-wrap'>
                  <div>
                    <Form.Label className='small text-muted mb-0'>From</Form.Label>
                    <Form.Control size='sm' type='date' value={inDateFrom} onChange={(e) => setInDateFrom(e.target.value)} />
                  </div>
                  <div>
                    <Form.Label className='small text-muted mb-0'>To</Form.Label>
                    <Form.Control size='sm' type='date' value={inDateTo} onChange={(e) => setInDateTo(e.target.value)} />
                  </div>
                  <Button size='sm' onClick={() => loadInboundReport()} disabled={loadingInbound}>
                    {loadingInbound ? 'Loading…' : 'Apply'}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {inboundError && <Alert variant='danger'>{inboundError}</Alert>}
                {!inboundError && inboundReport && inboundReport.overall.total === 0 && (
                  <div className='text-muted text-center py-4'>No inbound calls in this range.</div>
                )}
                {inboundReport && inboundReport.overall.total > 0 && (
                  <>
                    <div className='d-flex flex-wrap align-items-center justify-content-between text-center gap-4 mb-3'>
                      {[
                        { label: 'Total', value: inboundReport.overall.total },
                        { label: 'Answered', value: inboundReport.overall.answered },
                        { label: 'Abandoned', value: `${inboundReport.overall.abandoned} (${inboundReport.overall.abandon_pct}%)` },
                        { label: 'Avg wait', value: `${inboundReport.overall.avg_wait_sec}s` },
                        { label: 'Service level', value: `${inboundReport.overall.service_level}%` },
                      ].map((s) => (
                        <div key={s.label}>
                          <div className='fw-bold fs-3'>{s.value}</div>
                          <div className='text-muted small'>{s.label}</div>
                        </div>
                      ))}
                    </div>
                    {inboundReport.groups.length > 0 && (
                      <Table size='sm' responsive hover className='align-middle border-top'>
                        <thead>
                          <tr>
                            <th>In-group</th>
                            <th className='text-end'>Total</th>
                            <th className='text-end'>Answered</th>
                            <th className='text-end'>Abandoned</th>
                            <th className='text-end'>Avg wait</th>
                            <th className='text-end'>Service level</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inboundReport.groups.map((g) => (
                            <tr key={g.in_group}>
                              <td className='fw-semibold'>{g.in_group}</td>
                              <td className='text-end'>{g.total}</td>
                              <td className='text-end'>{g.answered}</td>
                              <td className='text-end'>{g.abandoned}</td>
                              <td className='text-end'>{g.avg_wait_sec}s</td>
                              <td className='text-end'>{g.service_level}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          )}
        </>
      )}
        </Tab>
      </Tabs>

      <Modal
        show={Boolean(pendingCampaignChange)}
        onHide={() => !changingCampaign && setPendingCampaignChange(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='d-flex align-items-center gap-2'>
            <i className='bi bi-arrow-left-right text-primary' />
            Change agent campaign
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingCampaignChange && (
            <>
              <div className='mb-2'>
                Move <span className='fw-semibold'>{pendingCampaignChange.agentName}</span>{' '}
                <span className='text-muted'>({pendingCampaignChange.agentUser})</span> from{' '}
                <Badge bg='light' text='dark' className='border'>{pendingCampaignChange.fromCampaign || '—'}</Badge>{' '}
                <i className='bi bi-arrow-right' />{' '}
                <Badge bg='success'>{pendingCampaignChange.toCampaign}</Badge>
              </div>
              <div className='rounded-3 p-3' style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                <div className='fw-semibold mb-1'>
                  <i className='bi bi-exclamation-triangle-fill me-1' />
                  The agent will be logged out
                </div>
                <div className='small'>
                  PolicyDial can't switch campaigns live, so the agent is logged out and must
                  <strong> reconnect PolicyDial</strong> (one click) to join the new campaign.
                  Any call in progress will be ended.
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' disabled={changingCampaign} onClick={() => setPendingCampaignChange(null)}>
            Cancel
          </Button>
          <Button variant='primary' disabled={changingCampaign} onClick={confirmChangeCampaign}>
            <i className='bi bi-arrow-left-right me-1' />
            {changingCampaign ? 'Moving…' : 'Move & log out'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(pendingSupervisorAction)}
        onHide={() => !runningSupervisorAction && setPendingSupervisorAction(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='d-flex align-items-center gap-2'>
            <i className='bi bi-exclamation-triangle-fill text-warning' />
            {pendingActionTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='fw-semibold mb-2'>
            {pendingActionAgents.length} agent{pendingActionAgents.length === 1 ? '' : 's'} selected
          </div>
          <div className='text-muted mb-3'>
            {pendingActionDescription}
          </div>
          <div className='d-flex flex-wrap gap-2 mb-3'>
            {pendingActionAgents.slice(0, 6).map((agentUser) => (
              <Badge key={agentUser} bg='light' text='dark' className='border'>
                {agentUser}
              </Badge>
            ))}
            {pendingActionAgents.length > 6 && (
              <Badge bg='light' text='dark' className='border'>
                +{pendingActionAgents.length - 6} more
              </Badge>
            )}
          </div>
          <div className='rounded-3 p-3' style={pendingActionNoticeStyle}>
            <div className='fw-semibold mb-1'>
              {pendingLiveRiskCount > 0
                ? `${pendingLiveRiskCount} selected agent${pendingLiveRiskCount === 1 ? '' : 's'} are in live call states`
                : 'No selected agents are currently in live call states'}
            </div>
            <div className='small' style={{ color: 'inherit' }}>
              {pendingActionType === 'logout'
                ? 'Use this only when the selected agents need to be removed immediately or their PolicyDial sessions are stuck.'
                : pendingActionType === 'pause'
                  ? 'Pause is useful when you need to temporarily stop multiple agents from receiving or placing calls.'
                  : 'Resume will attempt to return the selected agents to an active PolicyDial state.'}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='light'
            disabled={runningSupervisorAction}
            onClick={() => setPendingSupervisorAction(null)}
          >
            Cancel
          </Button>
          <Button
            variant={pendingActionPrimaryVariant}
            disabled={runningSupervisorAction}
            onClick={confirmSupervisorAction}
          >
            {pendingActionType === 'logout' ? (
              <i className='bi bi-box-arrow-right me-1' />
            ) : pendingActionType === 'pause' ? (
              <i className='bi bi-pause-circle me-1' />
            ) : (
              <i className='bi bi-play-circle me-1' />
            )}
            {runningSupervisorAction ? 'Working...' : pendingActionPrimaryLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
